import { Suspense, useCallback, useEffect, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Menu } from "lucide-react";
import { fetchOnboardingStatus } from "../../lib/api";
import { onboardingRoute } from "../../lib/routes";
import { setTimezone } from "../../lib/datetime";
import { useBackgroundActivityToasts } from "../../hooks/useBackgroundActivityToasts";
import { Dock } from "../dock/Dock";
import { DockRail } from "../dock/DockRail";
import { useDockRailOwnership } from "../../stores/dockRail";
import { Sidebar } from "./Sidebar";
import { NodeMovedBanner } from "./NodeMovedBanner";
import { NodeUnreachableBanner } from "./NodeUnreachableBanner";
import { ErrorBoundary } from "../common/ErrorBoundary";
import { Skeleton } from "../common/Skeleton";
import { ToastViewport } from "../common/ToastViewport";
import { toCssPx } from "../../lib/uiScale";
import styles from "./Shell.module.css";

// Sidebar width is user-adjustable via the drag handle on its right edge.
// Persisted so a wider tree survives refresh. Bounds match Obsidian/VSCode
// roughly — narrow enough to stay usable, wide enough for deep vault paths.
const SIDEBAR_MIN = 200;
/** Dragged to the minimum the panel does not disappear - it becomes an icon
 *  rail (owner, 2026-09-12). Wide enough for a 20px icon with the same optical
 *  padding the expanded rows have, so the icon column does not shift sideways
 *  when you collapse it. */
const SIDEBAR_RAIL = 64;
/** Below this the drag snaps to the rail. Sits under SIDEBAR_MIN so the usable
 *  range is continuous: 200..640 free, anything narrower means "rail". */
const SIDEBAR_SNAP = 168;
const SIDEBAR_MAX = 640;
/** 256 was cramped (owner, 2026-09-12) — and it is the width the mode
 *  buttons have to lay out in, so it decided their arrangement too. At 380 the
 *  four sit as a comfortable 2x2; one row of four needs about 590 (four
 *  140px tracks plus gaps and padding), which is a very wide panel for a
 *  permanent fixture. */
const SIDEBAR_DEFAULT = 380;
/** Widths this store has ever SHIPPED as its default. A value equal to one of
 *  these with no "the user chose it" marker beside it is this store's own past
 *  default echoed back, not a preference — so it is forgotten and the current
 *  default applies. Same rule the UI-scale store learned the hard way
 *  (CLAUDE.md: "a default is applied, never persisted"): without it every
 *  device that has ever opened the app stays pinned to 256 and a new default
 *  reaches nobody. */
const SIDEBAR_PRIOR_DEFAULTS = [256];
const SIDEBAR_CHOSEN_KEY = "agnt-sidebar-width-chosen";
const SIDEBAR_STORAGE_KEY = "agnt-sidebar-width";
const SIDEBAR_RAIL_KEY = "agnt-sidebar-rail";

/** Per-device, like the width beside it and like the theme: a laptop and a
 *  wall monitor want different answers. Absent/unreadable storage reads as
 *  "expanded", which is the state the app shipped with. */
function readSidebarRail(): boolean {
	try {
		return localStorage.getItem(SIDEBAR_RAIL_KEY) === "1";
	} catch {
		/* localStorage unavailable */
	}
	return false;
}

function readSidebarWidth(): number {
	try {
		const raw = localStorage.getItem(SIDEBAR_STORAGE_KEY);
		const chosen = localStorage.getItem(SIDEBAR_CHOSEN_KEY) === "1";
		if (raw) {
			const n = parseInt(raw, 10);
			if (!Number.isNaN(n) && n >= SIDEBAR_MIN && n <= SIDEBAR_MAX) {
				const isOldDefault = n === SIDEBAR_DEFAULT || SIDEBAR_PRIOR_DEFAULTS.includes(n);
				if (chosen || !isOldDefault) return n;
			}
		}
	} catch {
		/* localStorage unavailable */
	}
	return SIDEBAR_DEFAULT;
}

function RouteFallback() {
	return (
		<div style={{ padding: 36, display: "flex", flexDirection: "column", gap: 12 }}>
			<Skeleton width={220} height={32} />
			<Skeleton width="100%" height={120} />
			<Skeleton width="100%" height={80} />
		</div>
	);
}

export function Shell() {
	const location = useLocation();
	const navigate = useNavigate();
	const [checked, setChecked] = useState(false);
	const [mobileNavOpen, setMobileNavOpen] = useState(false);
	const [sidebarWidth, setSidebarWidth] = useState<number>(readSidebarWidth);
	const [sidebarRail, setSidebarRail] = useState<boolean>(readSidebarRail);
	// A page with its own full-height left tree renders the rail itself; Shell
	// then renders none, so the tree can reach the top of the window.
	const railClaimed = useDockRailOwnership((s) => s.claimed);
	const menuBtnRef = useRef<HTMLButtonElement>(null);
	const sidebarWrapperRef = useRef<HTMLDivElement>(null);
	const resizingRef = useRef(false);

	// Apply the sidebar width to the CSS variable on <html> so every
	// consumer of --sidebar-width (the Sidebar's own CSS, and anything
	// else downstream) reflows live as the user drags.
	useEffect(() => {
		// The rail is a NARROWER sidebar, not a hidden one: same element, same
		// mobile drawer rules, icons instead of rows. Nothing is display:none'd
		// at this level, so every measurement that reads the variable stays
		// honest about how much room the panel actually has.
		document.documentElement.style.setProperty(
			"--sidebar-width", sidebarRail ? `${SIDEBAR_RAIL}px` : `${sidebarWidth}px`,
		);
	}, [sidebarWidth, sidebarRail]);

	// The two ways a person actually chooses a width. There is deliberately no
	// unconditional write on mount any more — that is what pinned every device
	// to the old default.
	const chooseWidth = useCallback((w: number) => {
		setSidebarWidth(w);
		try {
			localStorage.setItem(SIDEBAR_CHOSEN_KEY, "1");
		} catch {
			/* localStorage unavailable */
		}
	}, []);

	useEffect(() => {
		try {
			localStorage.setItem(SIDEBAR_RAIL_KEY, sidebarRail ? "1" : "0");
		} catch {
			/* localStorage unavailable */
		}
	}, [sidebarRail]);

	// Persist on change (debounced via requestAnimationFrame to avoid
	// localStorage writes on every mousemove tick).
	useEffect(() => {
		const id = window.requestAnimationFrame(() => {
			try {
				localStorage.setItem(SIDEBAR_STORAGE_KEY, String(sidebarWidth));
			} catch {
				/* ignore */
			}
		});
		return () => window.cancelAnimationFrame(id);
	}, [sidebarWidth]);

	const onResizerDown = useCallback((e: React.MouseEvent) => {
		e.preventDefault();
		resizingRef.current = true;
		const startX = e.clientX;
		// The handle must track the cursor from where it IS. In rail mode the
		// panel is 64px wide, so starting from the remembered expanded width
		// made a drag out of the rail jump by the difference.
		const startW = sidebarRail ? SIDEBAR_RAIL : sidebarWidth;
		// Disable text selection + show resize cursor globally for the
		// duration of the drag; restore on mouseup.
		const prevUserSelect = document.body.style.userSelect;
		const prevCursor = document.body.style.cursor;
		document.body.style.userSelect = "none";
		document.body.style.cursor = "col-resize";
		const onMove = (ev: MouseEvent) => {
			if (!resizingRef.current) return;
			// Pointer deltas are viewport px; the width is the sidebar's own CSS px,
			// which the app UI scale (zoom on <html>) renders larger — convert.
			const delta = toCssPx(ev.clientX - startX);
			const raw = startW + delta;
			// Dragging past the snap point turns the panel into the icon rail
			// rather than refusing to go narrower; dragging back out restores the
			// width it had, which is why sidebarWidth is never set to the rail.
			if (raw < SIDEBAR_SNAP) {
				setSidebarRail(true);
				return;
			}
			setSidebarRail(false);
			const next = Math.max(SIDEBAR_MIN, Math.min(SIDEBAR_MAX, raw));
			chooseWidth(next);
		};
		const onUp = () => {
			resizingRef.current = false;
			document.body.style.userSelect = prevUserSelect;
			document.body.style.cursor = prevCursor;
			window.removeEventListener("mousemove", onMove);
			window.removeEventListener("mouseup", onUp);
		};
		window.addEventListener("mousemove", onMove);
		window.addEventListener("mouseup", onUp);
	}, [sidebarWidth, sidebarRail, chooseWidth]);

	// Double-click the resizer to reset to default width.
	const onResizerDouble = useCallback(() => chooseWidth(SIDEBAR_DEFAULT), [chooseWidth]);

	// Surface background agent work as toasts (KB ingest, memory consolidation,
	// LLM errors, etc.). Disabled while onboarding to avoid noise on first run.
	useBackgroundActivityToasts(checked && location.pathname !== "/onboarding");

	useEffect(() => {
		// Fetch server timezone on first mount (best-effort, non-blocking)
		fetch("/api/health")
			.then((r) => r.json())
			.then((d) => { if (d.timezone) setTimezone(d.timezone); })
			.catch(() => {/* browser tz fallback is fine */});

		// Skip if already on onboarding/settings, or already checked this session
		if (
			location.pathname === "/onboarding" ||
			location.pathname === "/settings" ||
			sessionStorage.getItem("agnt-onboard-checked")
		) {
			setChecked(true);
			return;
		}
		fetchOnboardingStatus()
			.then(({ onboarded }) => {
				sessionStorage.setItem("agnt-onboard-checked", "1");
				// Keep the address the person opened: onboarding offers to
				// continue there, instead of discarding a deep link on first visit.
				if (!onboarded)
					navigate(onboardingRoute(`${location.pathname}${location.search}${location.hash}`), { replace: true });
				setChecked(true);
			})
			.catch(() => setChecked(true));
	}, []); // only on first mount

	// Close mobile nav on route change
	useEffect(() => {
		setMobileNavOpen(false);
	}, [location.pathname]);

	// Mobile drawer affordances:
	//  - Escape closes
	//  - Body scroll is locked while open (otherwise iOS scrolls behind it)
	//  - Focus moves into the drawer on open and back to the menu button on close
	//
	// We deliberately do NOT implement a hard focus trap (Tab cycle) here —
	// it's complex to do correctly across portaled subtrees and the existing
	// backdrop click + Escape + auto-close on route change cover the realistic
	// dismissal paths. The drawer is also marked role="dialog" aria-modal so
	// screen readers know to constrain navigation.
	useEffect(() => {
		if (!mobileNavOpen) return;

		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				e.preventDefault();
				setMobileNavOpen(false);
			}
		};
		document.addEventListener("keydown", onKey);

		const prevOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";

		// Move focus into the drawer so the very next Tab lands on a nav item.
		const firstFocusable = sidebarWrapperRef.current?.querySelector<HTMLElement>(
			"button, [href], input, [tabindex]:not([tabindex='-1'])",
		);
		firstFocusable?.focus();

		return () => {
			document.removeEventListener("keydown", onKey);
			document.body.style.overflow = prevOverflow;
			// Return focus to the trigger so keyboard users don't lose their place.
			menuBtnRef.current?.focus();
		};
	}, [mobileNavOpen]);

	if (!checked) return null;

	return (
		<div
			className={styles.shell}
			data-mobile-nav={mobileNavOpen ? "open" : "closed"}
			data-sidebar-rail={sidebarRail ? "true" : "false"}
		>
			{/* Skip link — WCAG 2.4.1: keyboard users can bypass the sidebar
			    and jump directly to the page content. Visually hidden until
			    focused, then appears at the top of the viewport. */}
			<a href="#main-content" className={styles.skipLink}>
				Skip to main content
			</a>
			<button
				ref={menuBtnRef}
				type="button"
				className={styles.mobileMenuBtn}
				onClick={() => setMobileNavOpen((v) => !v)}
				aria-label={mobileNavOpen ? "Close navigation" : "Open navigation"}
				aria-expanded={mobileNavOpen}
				aria-controls="app-sidebar"
			>
				<Menu size={18} />
			</button>
			{mobileNavOpen && (
				<button
					type="button"
					className={styles.mobileNavBackdrop}
					onClick={() => setMobileNavOpen(false)}
					aria-label="Close navigation"
				/>
			)}
			<div ref={sidebarWrapperRef} id="app-sidebar" style={{ display: "contents" }}>
				<Sidebar />
			</div>
			{/* Drag handle. Present in BOTH states: the rail is a narrower
			    sidebar, so dragging it back out is the natural way to return and
			    the handle must not vanish when you reach the rail. Double-click
			    resets the width. The toggle rides ON the handle - one edge, one
			    set of controls - and stops mousedown so pressing it never starts
			    a drag. Hidden on mobile, where the sidebar is a drawer the
			    hamburger opens. */}
			<div
				className={styles.sidebarResizer}
				onMouseDown={onResizerDown}
				onDoubleClick={onResizerDouble}
				title="Drag to resize · double-click to reset"
			>
				<span role="separator" tabIndex={0} aria-orientation="vertical" aria-label="Resize sidebar" aria-valuemin={64} aria-valuemax={SIDEBAR_MAX} aria-valuenow={sidebarRail ? 64 : sidebarWidth}
          className={styles.resizeKeyboard} onKeyDown={(event) => {
            if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
            event.preventDefault(); setSidebarRail(false);
            chooseWidth(event.key === "Home" ? SIDEBAR_MIN : event.key === "End" ? SIDEBAR_MAX : Math.max(SIDEBAR_MIN, Math.min(SIDEBAR_MAX, sidebarWidth + (event.key === "ArrowRight" ? 20 : -20))));
          }} />
        <button
          type="button"
          className={styles.collapseBtn}
					onMouseDown={(e) => e.stopPropagation()}
					onDoubleClick={(e) => e.stopPropagation()}
					onClick={() => setSidebarRail((v) => !v)}
					aria-label={sidebarRail ? "Expand navigation" : "Collapse navigation to icons"}
					aria-expanded={!sidebarRail}
					aria-controls="app-sidebar"
					title={sidebarRail ? "Expand navigation" : "Collapse to icons"}
				>
					{sidebarRail ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
				</button>
			</div>
			<main id="main-content" className={styles.main}>
				{/* N3: the node that serves this UI stopped answering — say so, with
				    the options, instead of letting every action fail one by one. */}
				<NodeUnreachableBanner />
				{/* N2: this AgntSpace is parked — its vault moved to a node; where, and Roll back. */}
				<NodeMovedBanner />
				{/* The dock launcher — always present, in EVERY module, exactly as
				    in chat (owner rule 2026-08-12): the icons are how results and
				    tools are reached, so they cannot be a per-page feature. */}
				{!railClaimed && <DockRail />}
				{/* No app-level back control here, deliberately (owner rule
				    2026-08-15). A Shell-rendered `navigate(-1)` sat OUTSIDE the
				    module it was shown over, duplicated the back links modules
				    already had, and could carry you out of the module. Back is
				    module chrome: sub-pages pass `back` to PageHeader (or render
				    <BackLink> directly when they have no PageHeader) and it
				    points one level UP inside that module. Module roots get
				    none — the sidebar is the navigation at that level. */}
				<ErrorBoundary resetKey={location.key}>
					<Suspense fallback={<RouteFallback />}>
						<Outlet />
					</Suspense>
				</ErrorBoundary>
			</main>
			{/* The dock — the app-wide right-hand result/canvas surface (Rule 29).
			    Mounted at the Shell so EVERY module can push results into it
			    (stores/dock showResultInDock) and summon it; renders nothing
			    while hidden. Previously chat-only. */}
			<Dock />
			<ToastViewport />
		</div>
	);
}
